import React, { useState, useEffect, useMemo } from 'react';
import { DataGrid, GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import useFetch from '../../../services/useFetch.js';
import DeleteIcon from '@mui/icons-material/Delete';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DeleteData from '../../../services/DeleteData.js';
import PostDataRetData from '../../../services/PostDataRetData.js';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Box, Button, Chip, CircularProgress, Drawer, Stack, TextField, Typography } from '@mui/material';
import AddSku from './AddSku.jsx';
import SnackbarEx from '../../Helpers/SnackbarEx.jsx';
import ConfirmDelete from '../../Menu/HelperDialogs/ConfirmDelete.jsx';
import PageHeader from '../../layout/PageHeader';
import AppDialog from '../../ui/AppDialog.jsx';
import { dataGridSx, quickFilterSx } from '../../ui/dataGridStyles.js';
import { withResizableColumns, useColumnWidths } from '../../ui/ResizableGridHeader.jsx';
import { tokens } from '../../../theme.js';

const DEFAULT_WIDTHS = {
  skuCode: 160,
  iName: 200,
  type: 120,
  batchId: 140,
  bottleOz: 90,
  status: 110,
  created: 120,
  itemAge: 100,
  forSale: 110,
};

function toDateInputValue(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function StatusChip({ label, tone = 'neutral' }) {
  const styles = {
    ok: { bg: tokens.okBg, color: tokens.accent },
    warn: { bg: tokens.warnBg, color: tokens.warn },
    danger: { bg: tokens.dangerBg, color: tokens.danger },
    neutral: { bg: tokens.bg, color: tokens.inkMuted },
  }[tone];

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        backgroundColor: styles.bg,
        color: styles.color,
        fontWeight: 650,
        border: 'none',
        height: 24,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

export default function ItemSku() {
  const [row, setRow] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState({
    color: '',
    message: '',
    open: false,
  });
  const [reloadData, setReloadData] = useState(false);
  const [selIngrIdArr, setSelIngrIdArr] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [openAddNewItem, setOpenAddNewItem] = useState(false);
  const [markingForSale, setMarkingForSale] = useState(false);
  const [editDateOpen, setEditDateOpen] = useState(false);
  const [editDateValue, setEditDateValue] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  const { widths, onWidthChange } = useColumnWidths(DEFAULT_WIDTHS);

  const { data: skuData, load } = useFetch('api/all-skus', reloadData);

  useEffect(() => {
    setRow(
      skuData?.map((e) => {
        const forSale = e.status === 'For Sale';
        return {
          id: e.itemId,
          skuCode: e.skuCode || '',
          iName: e.itemName,
          type: e.posOrSub,
          status: e.status,
          batchId: e.batchId || e.batchNumber || '',
          bottleOz: e.bottleOz ?? null,
          created: e.dateAdded,
          itemAge: e.dateAdded,
          forSale,
        };
      }) ?? [],
    );
  }, [skuData]);

  const baseColumns = useMemo(() => ([
    {
      field: 'skuCode',
      headerName: 'SKU',
      minWidth: 140,
      valueFormatter: ({ value }) => (value ? value : '—'),
    },
    { field: 'iName', headerName: 'Item', minWidth: 160 },
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 120,
      renderCell: (p) => (
        <span style={{ fontWeight: 600, fontSize: 13 }}>
          {p.value === 'pos' ? 'POS item' : 'Sub-assembly'}
        </span>
      ),
    },
    {
      field: 'batchId',
      headerName: 'Batch',
      minWidth: 120,
      valueFormatter: ({ value }) => (value ? value : '—'),
    },
    {
      field: 'bottleOz',
      headerName: 'Oz',
      minWidth: 70,
      valueFormatter: ({ value }) => (value != null ? value : '—'),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 100,
      renderCell: (params) => (
        <StatusChip
          label={params.value || '—'}
          tone={
            params.value === 'For Sale'
              ? 'ok'
              : params.value === 'Unsold'
                ? 'warn'
                : params.value === 'Sold'
                  ? 'danger'
                  : 'neutral'
          }
        />
      ),
    },
    {
      field: 'created',
      headerName: 'Created',
      minWidth: 110,
      type: 'date',
      valueGetter: (params) => (params.value ? new Date(params.value) : null),
      valueFormatter: ({ value }) => (
        value ? value.toLocaleDateString() : '—'
      ),
    },
    {
      field: 'itemAge',
      headerName: 'On shelf',
      minWidth: 90,
      valueGetter: (params) => {
        if (!params.value) return '—';
        const days = Math.floor((Date.now() - new Date(params.value)) / (1000 * 60 * 60 * 24));
        return `${days} ${days === 1 ? 'day' : 'days'}`;
      },
    },
    {
      field: 'forSale',
      headerName: 'Listed',
      minWidth: 100,
      renderCell: (params) => (
        <StatusChip
          label={params.value ? 'For sale' : 'Not listed'}
          tone={params.value ? 'ok' : 'danger'}
        />
      ),
    },
  ]), []);

  const columns = useMemo(
    () => withResizableColumns(baseColumns, widths, onWidthChange),
    [baseColumns, widths, onWidthChange],
  );

  const refreshPage = () => {
    setReloadData(true);
    setTimeout(() => setReloadData(false), 200);
  };

  const closeDrawer = (success) => {
    setOpenAddNewItem(false);
    if (success) {
      setOpenSnackbar({ color: 'success', message: 'SKU added successfully!', open: true });
      refreshPage();
    }
  };

  const selectClick = (ids) => {
    setSelIngrIdArr(Array.isArray(ids) ? ids : []);
  };

  const deleteIngredient = async (bool) => {
    setDeleteOpen(false);
    if (!bool) return;
    const { success } = await DeleteData('api/delete-skus', { itemIds: selIngrIdArr });
    if (success) {
      setSelIngrIdArr([]);
      setOpenSnackbar({
        color: 'success',
        message: 'SKUs deleted — materials returned to inventory',
        open: true,
      });
      refreshPage();
    }
  };

  const unsoldSelected = row.filter(
    (r) => selIngrIdArr.includes(r.id) && r.status !== 'For Sale',
  );
  const canMarkForSale = unsoldSelected.length > 0 && !markingForSale;
  const hasSelection = selIngrIdArr.length > 0;

  const openEditDate = () => {
    if (!hasSelection) return;
    const selected = row.filter((r) => selIngrIdArr.includes(r.id));
    const first = selected[0];
    setEditDateValue(toDateInputValue(first?.created) || toDateInputValue(new Date()));
    setEditDateOpen(true);
  };

  const saveCreatedDate = async () => {
    if (!hasSelection || !editDateValue) return;
    setSavingDate(true);
    const { data, error } = await PostDataRetData(
      { itemIds: selIngrIdArr, dateAdded: editDateValue },
      'api/skus/update-created-date',
    );
    setSavingDate(false);

    if (error || data?.error) {
      const msg =
        data?.error?.message
        || error?.response?.data?.error?.message
        || 'Could not update created date';
      setOpenSnackbar({ color: 'error', message: msg, open: true });
      return;
    }

    const count = data?.data?.modifiedCount ?? selIngrIdArr.length;
    setEditDateOpen(false);
    setOpenSnackbar({
      color: 'success',
      message: count === 1
        ? 'Created date updated'
        : `Created date updated on ${count} SKUs`,
      open: true,
    });
    refreshPage();
  };

  const markSelectedForSale = async () => {
    if (!canMarkForSale) return;
    setMarkingForSale(true);
    const { data, error } = await PostDataRetData(
      { itemIds: unsoldSelected.map((r) => r.id) },
      'api/skus/mark-for-sale',
    );
    setMarkingForSale(false);

    if (error || data?.error) {
      const msg =
        data?.error?.message
        || error?.response?.data?.error?.message
        || 'Could not mark SKUs for sale';
      setOpenSnackbar({ color: 'error', message: msg, open: true });
      return;
    }

    const count = data?.data?.updatedCount ?? unsoldSelected.length;
    setSelIngrIdArr([]);
    setOpenSnackbar({
      color: 'success',
      message: count === 1
        ? 'SKU marked for sale — product inventory updated'
        : `${count} SKUs marked for sale — product inventory updated`,
      open: true,
    });
    refreshPage();
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 'auto', flexWrap: 'wrap' }}>
          <button
            className={canMarkForSale ? 'recipe-bttn' : 'delete-dis'}
            disabled={!canMarkForSale}
            onClick={markSelectedForSale}
          >
            <StorefrontOutlinedIcon
              sx={{ color: canMarkForSale ? tokens.accent : '#a39b92', fontSize: 18 }}
            />
            <span>{markingForSale ? 'Updating…' : 'Mark for sale'}</span>
          </button>
          <button
            className={hasSelection ? 'recipe-bttn' : 'delete-dis'}
            disabled={!hasSelection}
            onClick={openEditDate}
          >
            <CalendarMonthOutlinedIcon
              sx={{ color: hasSelection ? tokens.accent : '#a39b92', fontSize: 18 }}
            />
            <span>Edit date</span>
          </button>
          <button
            className={hasSelection ? 'delete-bttn' : 'delete-dis'}
            disabled={!hasSelection}
            onClick={() => setDeleteOpen(true)}
          >
            <DeleteIcon sx={{ color: hasSelection ? tokens.danger : '#a39b92', fontSize: 18 }} />
            <span>Delete</span>
          </button>
          <button className="recipe-bttn" onClick={() => setOpenAddNewItem(true)}>
            <AddOutlinedIcon fontSize="small" sx={{ color: tokens.accent, fontSize: 18 }} />
            <span>Add SKU</span>
          </button>
        </Stack>
        <GridToolbarQuickFilter variant="outlined" size="small" sx={quickFilterSx} />
      </GridToolbarContainer>
    );
  }

  if (load && !skuData) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 280 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Item SKUs"
        subtitle="Bottle SKUs from a production batch, then mark them for sale to add stock to the linked product"
        actions={
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => setOpenAddNewItem(true)}
          >
            Add SKU
          </Button>
        }
      />

      <Drawer
        anchor="right"
        open={openAddNewItem}
        onClose={() => closeDrawer(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 440 },
            backgroundColor: tokens.surface,
            borderLeft: `1px solid ${tokens.border}`,
            boxShadow: '-12px 0 40px rgba(44, 36, 27, 0.08)',
          },
        }}
        BackdropProps={{
          sx: { backgroundColor: 'rgba(44, 36, 27, 0.18)' },
        }}
      >
        <AddSku close={closeDrawer} />
      </Drawer>

      <AppDialog
        open={editDateOpen}
        onClose={() => !savingDate && setEditDateOpen(false)}
        title="Edit created date"
        subtitle={
          selIngrIdArr.length === 1
            ? 'Set the original create date for this SKU (useful when migrating from a spreadsheet).'
            : `Apply the same create date to ${selIngrIdArr.length} selected SKUs.`
        }
        actions={
          <>
            <Button variant="outlined" onClick={() => setEditDateOpen(false)} disabled={savingDate}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveCreatedDate}
              disabled={savingDate || !editDateValue}
            >
              {savingDate ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save date'}
            </Button>
          </>
        }
      >
        <Stack spacing={1.5}>
          <TextField
            label="Created date"
            type="date"
            value={editDateValue}
            onChange={(e) => setEditDateValue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
          <Typography sx={{ color: tokens.inkMuted, fontSize: 13 }}>
            On-shelf age is calculated from this date.
          </Typography>
        </Stack>
      </AppDialog>

      {deleteOpen && <ConfirmDelete deleteItem={deleteIngredient} deleteOpen={deleteOpen} />}

      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, pb: 4 }}>
        <Box
          sx={{
            border: `1px solid ${tokens.border}`,
            borderRadius: 2,
            backgroundColor: tokens.surface,
            overflow: 'hidden',
            height: { xs: 'calc(100vh - 220px)', md: 'calc(100vh - 200px)' },
            minHeight: 420,
          }}
        >
          <DataGrid
            checkboxSelection
            rowSelectionModel={selIngrIdArr}
            onRowSelectionModelChange={selectClick}
            columns={columns}
            rows={row}
            slots={{ toolbar: CustomToolbar }}
            rowHeight={44}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            sx={{
              ...dataGridSx,
              border: 'none',
              height: '100%',
              '& .MuiDataGrid-columnHeader': {
                overflow: 'visible',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                overflow: 'visible',
              },
            }}
          />
        </Box>
      </Box>

      {openSnackbar.open && (
        <SnackbarEx
          color={openSnackbar.color}
          message={openSnackbar.message}
          open={openSnackbar.open}
          close={() => setOpenSnackbar({ color: '', message: '', open: false })}
        />
      )}
    </Box>
  );
}
